import React from 'react';
import WaitingRow from './WaitingRow';

/**
 * WaitingOnYouPanel — the only urgent panel on MY WORK (Epic 4 · S4.2).
 *
 * The `waiting_on_you` panel (`salience:100` when it has rows) is the one place
 * on the page that carries the act-on-this accent (Part C) — a 3px top border
 * in the brand blue `#32B1FF`, used on this panel ONLY. Nothing else on the
 * page is red/amber; this blue is the single urgency signal.
 *
 * Rows follow the weather-intelligence `ActionList.js` pattern (ranked text
 * rows + actions). One `WaitingRow` per item, in the order given — the panel
 * never re-sorts, never drops, never pages (§5.7). Degenerate cases:
 *   - 0 rows  → the panel is not rendered at all (the shell hides the empty
 *               state; returning `null` here keeps that contract local).
 *   - 1 row   → one row, no list chrome (no dividers between rows).
 *   - many    → all rows, no paging (operator-9 carries every row).
 *
 * `park` is shown on a row only in the multi-park (all-parks) view, so the
 * panel takes `parks` (the page's `parks[]`) to decide that.
 */
const WaitingOnYouPanel = ({ facts, parks, onOpenItem }) => {
  const items = Array.isArray(facts?.items) ? facts.items : [];
  if (items.length === 0) return null; // hidden entirely when empty (§5.7)

  const multiPark = Array.isArray(parks) && parks.length > 1;
  const single = items.length === 1;

  return (
    <section
      data-testid="panel-waiting_on_you"
      className="rounded-xl bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800 border-t-[3px] border-t-[#32B1FF] p-4"
    >
      <header className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="gi-label text-gray-500 dark:text-gray-400">Waiting on you</h3>
        {!single && (
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">
            {items.length}
          </span>
        )}
      </header>

      <ul className={single ? '' : 'divide-y divide-gray-100 dark:divide-gray-800'}>
        {items.map((item) => (
          <WaitingRow
            key={item?.id ?? item?.title}
            item={item}
            multiPark={multiPark}
            onOpenItem={onOpenItem}
          />
        ))}
      </ul>
    </section>
  );
};

export default WaitingOnYouPanel;
